import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import {
  InputComponent,
  TextareaComponent,
  EmailIconComponent,
  LocationIconComponent,
  PhoneIconComponent,
} from '@mars/ui-components';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoModule } from '@jsverse/transloco';
import { Pattern } from '../../constants/regex.constant';
import { ValidationMessageComponent } from '../validation-message/validation-message.component';
import {
  NgxIntlTelInputModule,
  SearchCountryField,
  CountryISO,
  PhoneNumberFormat,
  type ChangeData,
} from 'ngx-intl-tel-input';
import { ContactUsRequest } from '../../models/contact-us';
import { ApiErrorResponse } from '../../Interfaces/apiResponse.interface';
import { ContactUsService } from '../../services/contact-us/contact-us.service';
import { FormService } from '../../services/form/form.service';
import { ToasterService } from '@mars/ui-components';
import { TranslocoService } from '@jsverse/transloco';
import { finalize } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BrandingService } from '../../branding/branding.service';

@Component({
  selector: 'mars-contact-us-form',
  standalone: true,
  imports: [
    InputComponent,
    ReactiveFormsModule,
    TextareaComponent,
    TranslocoModule,
    EmailIconComponent,
    LocationIconComponent,
    PhoneIconComponent,
    ValidationMessageComponent,
    NgxIntlTelInputModule,
  ],
  templateUrl: './contact-us.component.html',
  styleUrl: './contact-us.component.scss',
})
export class ContactUsComponent {
  private readonly contactUsService = inject(ContactUsService);
  private readonly formService = inject(FormService);
  private readonly toastService = inject(ToasterService);
  private readonly translocoService = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly branding = inject(BrandingService);

  /** The replica's `country_code`, when the phone widget knows it. */
  protected readonly phoneCountry = computed(() => {
    const iso = this.branding.countryCode().toLowerCase() as CountryISO;
    return Object.values(CountryISO).includes(iso)
      ? iso
      : CountryISO.SaudiArabia;
  });

  private readonly lang = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  /**
   * The replica's `address` in the page's language, or the other
   * language when only one is set. `null` when it set neither, and the
   * template hides the row.
   */
  protected readonly address = computed(() => {
    const { en, ar } = this.branding.address();
    return this.lang() === 'ar' ? (ar ?? en) : (en ?? ar);
  });

  readonly SearchCountryField = SearchCountryField;
  readonly CountryISO = CountryISO;
  readonly PhoneNumberFormat = PhoneNumberFormat;

  protected loading = signal(false);

  form = new FormGroup({
    name: new FormControl('', [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(100),
    ]),
    email: new FormControl('', [
      Validators.required,
      Validators.pattern(Pattern.EMAIL),
    ]),
    phone: new FormControl<ChangeData | null>(null, Validators.required),
    message: new FormControl('', [
      Validators.required,
      Validators.minLength(10),
      Validators.maxLength(1000),
    ]),
  });

  submitForm() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { name, email, phone, message } = this.form.value;

    const payload: ContactUsRequest = {
      name: name!,
      email: email!,
      phone: phone!.e164Number!,
      message: message!,
    };
    this.loading.set(true);
    this.contactUsService
      .sendMessage(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loading.set(false))
      )
      .subscribe({
        next: () => {
          this.toastService.success(
            this.translocoService.translate(
              'contact_us_screen_form_success_message'
            )
          );
          this.form.reset();
        },
        error: (error: ApiErrorResponse) => {
          const apiError = error.errors;
          if (apiError) {
            this.formService.setApiFormErrors(apiError, this.form);
          }
        },
      });
  }
}
